import Compressor from 'compressorjs';
import * as Constants from "../constants";
import {mapWithConcurrency} from "@/services/concurrency";
import {getImageProcessingConcurrency} from "@/services/image-validation.service";

function compressImage(blob, options) {
    return new Promise((resolve, reject) => {
        new Compressor(blob, {
            quality: options.quality,
            maxWidth: options.maxWidth,
            maxHeight: options.maxHeight,
            convertSize: options.convertSize,
            // PNG stays PNG (unless convertSize says otherwise), everything else
            // (JPEG, HEIC, WEBP) becomes a JPEG the server can always read.
            mimeType: blob.type === 'image/png' ? 'image/png' : 'image/jpeg',
            success: resolve,
            error: reject
        });
    });
}

function shouldCompress(file, options) {
    if(file.isRemote || (file.meta && file.meta.compressed) || !file.type || !file.type.startsWith('image/') || !file.data) {
        return false;
    }

    return file.data.size > options.sizeThreshold;
}

export async function compressImageFile(file, options = Constants.IMAGE_COMPRESSION_OPTIONS) {
    if(!shouldCompress(file, options)) {
        return { file, changed: false };
    }

    const sourceBlob = file.data.type || !file.type
        ? file.data
        : file.data.slice(0, file.data.size, file.type);
    const compressedBlob = await compressImage(sourceBlob, options);
    const type = compressedBlob.type || file.type;

    if(compressedBlob.size >= file.data.size && type === file.type) {
        return { file, changed: false };
    }

    const name = type === 'image/jpeg' && !/\.jpe?g$/i.test(file.name)
        ? file.name.replace(/\.[^.]+$/, '') + '.jpg'
        : file.name;

    return {
        file,
        changed: true,
        data: compressedBlob,
        size: compressedBlob.size,
        type,
        name
    };
}

export async function compressFiles(uppy, fileIDs, options = Constants.IMAGE_COMPRESSION_OPTIONS, onError = null) {
    return mapWithConcurrency(fileIDs, getImageProcessingConcurrency(), async fileID => {
        const file = uppy.getFile(fileID);
        if(!file) {
            return null;
        }

        try {
            uppy.emit('preprocess-progress', file, {
                mode: 'indeterminate',
                message: 'Compressing image'
            });

            const result = await compressImageFile(file, options);
            const meta = { ...file.meta, compressed: true };

            if(!result.changed) {
                uppy.setFileState(fileID, { meta });
                return result;
            }

            uppy.setFileState(fileID, {
                data: result.data,
                size: result.size,
                type: result.type,
                name: result.name,
                meta: {
                    ...meta,
                    name: result.name,
                    type: result.type
                }
            });
            return result;
        } catch(error) {
            uppy.log(`[Image compression] Failed to compress ${fileID}`, 'warning');
            uppy.log(error, 'warning');
            if(onError) {
                onError(file, error);
            }
            return { file, error };
        } finally {
            const nextFile = uppy.getFile(fileID);
            if(nextFile) {
                uppy.emit('preprocess-complete', nextFile);
            }
        }
    });
}

/**
 * Compresses images before upload, a few at a time. `options` defaults to the
 * header settings; pass Constants.TILE_COMPRESSION_OPTIONS for tile images.
 * `onError(file, error)` is called when a file could not be compressed (it is
 * then uploaded unchanged).
 */
export function addBalancedImageCompression(uppy, options = Constants.IMAGE_COMPRESSION_OPTIONS, onError = null) {
    uppy.addPreProcessor(async fileIDs => {
        await compressFiles(uppy, fileIDs, options, onError);
    });

    return uppy;
}
